<script setup lang="ts">

import { computed, onMounted, ref, provide } from 'vue'
import DefaultLayout from '@/Layouts/DefaultLayout.vue'
import TheHeader from '@/Components/TheHeader.vue'
import TheListings from '@/Pages/the-city/components/TheListings.vue'
import { partialReload } from '@/Composables/use-listing-search'
import { CityPageProps } from '@/Pages/the-city/interfaces/City'
import ExploreSection from '@/Pages/the-city/components/ExploreSection/ExploreSection.vue'
import FeatureSection from '@/Pages/the-city/components/ExploreSection/FeatureSection.vue'
import CityInfo from '@/Pages/the-city/components/CityInfo.vue'
import SeoTable from '@/Pages/the-city/components/SeoTable.vue'
import TheFaq from '@/Pages/the-city/components/TheFaq/TheFaq.vue'
import SpecialGuides from '@/Pages/the-city/components/SpecialGuides.vue'
import NearbyCities from '@/Pages/the-city/components/NearbyCities.vue'
import HiddenGemz from '@/Components/hidden-gemz/HiddenGemz.vue'
import TheBreadcrumbs from '@/Components/the-breadcrumbs/TheBreadcrumbs.vue'
import TheDescription from '@/Pages/the-city/components/TheDescription/TheDescription.vue'
import { toggleIsUserLogged } from '@/Composables/authorization'
import PageEditLink from '@/Components/PageEditLink.vue'
import { useCurrencyWatcher } from '@/Composables/use-currency'
import { router, WhenVisible } from '@inertiajs/vue3'
import TheCityAds from '@/Pages/the-city/components/AdFeatures/TheCityAds.vue'
import CcRelatedArticles from '@/Pages/content-cluster/RelatedArticles.vue'
import { CityInfo as ccCityInfoType } from '@/Pages/content-cluster/interfaces/ContentCluster'
import FeaturedArticles from '@/Pages/the-city/components/FeaturedArticles.vue'

const props = defineProps<CityPageProps>()

provide('isHideSorting', props.isHideSorting)

onMounted(() => {
    if (!props.listingsData) {
        partialReload()
    }

    if (props.user) {
        toggleIsUserLogged(true)
    } else {
        toggleIsUserLogged(false)
    }
})

const headerContent = computed(() => {
    return props.listingsData ? props.listingsData.updatedHeaderInfo : props.headerInfo
})

useCurrencyWatcher((currency) => {
    router.reload({
        only: ['exploreSection', 'activeCurrency', 'featureSection'],
        data: {
            currency: currency
        },
        method: 'post'
    })
})

const isFiltersShown = ref(false)

const openFilter = (): void => {
    isFiltersShown.value = true
}

const closeFilter = (): void => {
    isFiltersShown.value = false
}

const switchFilter = (): void => {
    isFiltersShown.value = !isFiltersShown.value
}

provide('cityName', computed(() => props.cityName))
provide('countryName', computed(() => props.countryName))

const ccCityInfo = computed((): ccCityInfoType | undefined => {
    return {
        city: props.cityName,
        url: props.countryName
    }
})

</script>

<template>
    <DefaultLayout>
        <template #header>
            <TheHeader
                :location="location"
                with-search
                :auto-open-calendar="true"
            />
        </template>

        <div class="container city-header mb-3 mb-lg-5 mt-3">
            <TheBreadcrumbs
                :breadcrumbs="breadcrumbs"
            />
        </div>

        <template v-if="user?.canEdit && editLinks">
            <PageEditLink
                v-for="(item, key) in editLinks"
                :key
                :edit-link="item.url"
                :text="item.text"
                class="text-primary"
            />
        </template>

        <FeatureSection v-if="featureSection?.items?.length" :="featureSection" />

        <TheListings
            :listings-data="listingsData"
            :loading="loading"
            :is-filters-shown="isFiltersShown"
            :header-info="headerContent"
            :districts="districts"
            :description-name="description?.name"
            :location-search="location.search"
            :text-after-title-for="props.textAfterTitleFor"
            :alink="alink"
            @close-filter="closeFilter"
            @open-filter="openFilter"
            @switch-filter="switchFilter"
        />

        <div class="container">
            <CcRelatedArticles v-if="ccCityInfo" :articles="contentCluster" :city-info="ccCityInfo" />
        </div>

        <div v-if="hiddenGemz && hiddenGemz.length" class="container">
            <div class="row">
                <div class="col">
                    <HiddenGemz
                        v-for="(hg, key) in hiddenGemz"
                        :key
                        :="hg"
                        :user="user"
                        class="py-5 px-4 mb-5 mt-5 mt-lg-0"
                    />
                </div>
            </div>
        </div>

        <ExploreSection :="exploreSection" />

        <section v-if="cityInfo || description" class="bg-white py-5 py-lg-6">
            <div class="container">
                <div class="row">
                    <div class="col-12 col-lg-8 pr-lg-6">
                        <CityInfo v-if="cityInfo" :city-info="cityInfo" />

                        <TheDescription v-else-if="description" :description="description" />

                        <SeoTable v-if="seoTable" :="seoTable" />

                        <FeaturedArticles :articles="articles" />

                        <TheFaq v-if="faq" :="faq" />
                    </div>

                    <div class="col-12 col-lg-4">
                        <!-- <WhenVisible v-if="alink" data="alink">
                            <template #fallback>
                                <div class="d-flex justify-content-center spinner-wrap">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="sr-only">Loading...</span>
                                    </div>
                                </div>
                            </template>
                            <TheCityAds :="alink" />
                        </WhenVisible>-->

                        <div class="stickyPosition">
                            <SpecialGuides :="specialGuides" />

                            <NearbyCities v-if="nearByCities" :items="nearByCities" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </defaultlayout>
</template>

<style lang="scss" scoped>
.stickyPosition {
    @media (min-width: 992px) {
        position: sticky;
        top: 50px;
    }

}
</style>